import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  Bell,
  Building2,
  CalendarDays,
  CalendarRange,
  Clock,
  FileCheck2,
  LogIn,
  LogOut,
} from "lucide-react";
import { Link } from "react-router-dom";

import { AnnouncementsCard } from "@/components/me/AnnouncementsCard";
import { LocationCheckCard } from "@/components/me/LocationCheckCard";
import { PunchDialog, type PunchKind } from "@/components/me/PunchDialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/auth/AuthContext";
import { useEmployeeUnreadNotifications } from "@/hooks/useUnreadNotifications";
import type { GeoPosition } from "@/hooks/useGeolocation";
import { ApiError } from "@/lib/api";
import { formatDateTimeIn, formatTimeIn, isoDateIn } from "@/lib/datetime";
import {
  employeeApi,
  type EmployeeConfig,
  type EmployeeDay,
  type EmployeeSchedule,
  type EmployeeSession,
  type EmployeeSite,
} from "@/lib/employee-api";
import {
  accuracyLabel,
  dayHeading,
  nextAction,
  projectedShortfall,
  scheduleLabel,
  statusLabel,
  summariseToday,
  type TodaySummary,
} from "@/lib/today-status";
import { formatDuration, shortDayReasons } from "@/lib/work-hours";

/** Minutes → "7h 30m", the shape people read a workday in. */
function duration(mins: number | null): string {
  if (mins == null || mins <= 0) return "—";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h ? `${h}h ${m}m` : `${m}m`;
}

/** The status pill's colour, from the same summary its words come from. */
function statusTone(summary: TodaySummary): string {
  if (summary.phase === "checked-in") return "border-success/30 text-success";
  if (summary.phase === "checked-out") {
    return summary.isShort ? "border-warning/40 text-warning" : "border-info/30 text-info";
  }
  return "border-border text-muted-foreground";
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-sm font-semibold tabular-nums">{value}</p>
    </div>
  );
}

function SessionRow({ session, tz }: { session: EmployeeSession; tz: string }) {
  return (
    <div className="flex items-center justify-between border-b py-2 last:border-b-0">
      <div className="flex items-center gap-2 text-sm">
        <Clock className="h-4 w-4 text-muted-foreground" />
        <span>{formatTimeIn(session.startAt, tz)}</span>
        <span className="text-muted-foreground">→</span>
        <span>{session.endAt ? formatTimeIn(session.endAt, tz) : "in progress"}</span>
      </div>
      <div className="flex items-center gap-2">
        {session.workMode === "WFH" && <Badge variant="secondary">WFH</Badge>}
        {session.workMode === "WFA" && <Badge variant="secondary">WFA</Badge>}
        {/* Surfaced because it changes how the punch was treated: a coarse fix
            can never count as inside the geofence (BR-152). */}
        {session.startGpsStatus === "INVALID" && (
          <Badge variant="outline" className="text-warning">
            Low accuracy {accuracyLabel(session.startAccuracyM)}
          </Badge>
        )}
        <span className="text-sm text-muted-foreground">
          {duration(session.durationMinutes)}
        </span>
      </div>
    </div>
  );
}

export default function MyDay() {
  const { employee } = useAuth();
  const { toast } = useToast();
  const [config, setConfig] = useState<EmployeeConfig | null>(null);
  const [day, setDay] = useState<EmployeeDay | null>(null);
  /**
   * The open session on any date, from `/mobile/attendance/current`. A night
   * shift started yesterday sits on yesterday's record, so today's alone would
   * read "Not checked in" after midnight — the app has always asked this.
   */
  const [current, setCurrent] = useState<EmployeeSession | null>(null);
  /** Null when it could not be read: today's hours are then simply not shown. */
  const [schedule, setSchedule] = useState<EmployeeSchedule | null>(null);
  /** Moves the running hours on while a session is open. */
  const [now, setNow] = useState(() => new Date());
  /** Where the employee may clock in, for the confirmation's map. */
  const [sites, setSites] = useState<EmployeeSite[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  /**
   * The open confirmation, if any (FEAT-2026-025). Check in and check out ask
   * first, as the app does, instead of acting on the press and only stopping
   * to warn — and the warnings themselves are now the server's (BR-164).
   */
  const [punch, setPunch] = useState<PunchKind | null>(null);
  const unread = useEmployeeUnreadNotifications();

  const tz = config?.tenant.timezone ?? "Asia/Kuala_Lumpur";
  // BR-159. The day's state comes from its sessions, not from "is one open?"
  // alone — that read a finished day as "Not checked in" (BUG-2026-013).
  const summary = useMemo(() => summariseToday(day, current, now), [day, current, now]);
  const openSession = summary.openSession;
  const action = nextAction(summary.phase, config?.attendance.mode ?? "SINGLE_SESSION_PER_DAY");

  const checkedIn = summary.phase === "checked-in";
  useEffect(() => {
    if (!checkedIn) return;
    const id = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(id);
  }, [checkedIn]);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const [cfg, today, open, sched, siteList] = await Promise.all([
        employeeApi.config(),
        employeeApi.today(),
        employeeApi.current().catch(() => ({ session: null })),
        // Secondary: without it the card just doesn't show today's hours.
        employeeApi.schedule().catch(() => null),
        // Secondary too: without it the confirmation shows no map.
        employeeApi.sites().catch(() => ({ data: [] as EmployeeSite[] })),
      ]);
      setConfig(cfg);
      setDay(today);
      setCurrent(open.session);
      setSchedule(sched);
      setSites(siteList.data);
      setNow(new Date());
    } catch (e) {
      setLoadError(e instanceof ApiError ? e.message : "Could not load today's attendance");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /** Re-read today and the open session after a punch. */
  const refreshDay = useCallback(async () => {
    try {
      const [today, open] = await Promise.all([employeeApi.today(), employeeApi.current()]);
      setDay(today);
      setCurrent(open.session);
      setNow(new Date());
    } catch {
      // The punch already succeeded; what it returned stands until the next load.
    }
  }, []);

  /** A punch the confirmation made: show it, and read today again. */
  const onPunched = useCallback(
    (updated: EmployeeDay, kind: PunchKind, position: GeoPosition) => {
      // Checking out an overnight session returns *yesterday's* day, which
      // must not stand in for today's; the refresh below reads today again.
      if (updated.date === day?.date) setDay(updated);
      setCurrent(null);
      setNow(new Date());
      setPunch(null);
      toast({
        title: kind === "out" ? "Checked out" : "Checked in",
        description: `${accuracyLabel(Math.round(position.accuracy))} accuracy`,
      });
      void refreshDay();
    },
    [day, refreshDay, toast],
  );

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (loadError) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Couldn't load your day</AlertTitle>
        <AlertDescription className="flex items-center gap-3">
          {loadError}
          <Button size="sm" variant="outline" onClick={() => void load()}>
            Try again
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  const webEnabled = config?.attendance.webCheckInEnabled ?? false;
  const scheduleToday = schedule?.days.find((d) => d.date === day?.date) ?? null;
  const hoursLabel = scheduleLabel(scheduleToday);
  const notScheduled = scheduleToday != null && !scheduleToday.working;
  const shortReasons = summary.isShort
    ? shortDayReasons({
        durationMinutes: day?.totalDurationMinutes ?? null,
        shortByMinutes: summary.shortByMinutes,
        leftEarlyByMinutes: summary.leftEarlyByMinutes,
      })
    : [];
  /** A time today reads as a time; an overnight start from yesterday keeps its date. */
  const clock = (iso: string | null) =>
    !iso
      ? "—"
      : isoDateIn(iso, tz) === day?.date
        ? formatTimeIn(iso, tz)
        : formatDateTimeIn(iso, tz);

  return (
    <div className="space-y-4">
      <Card data-testid="today-status">
        <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-3">
          <div className="space-y-0.5">
            <CardTitle className="text-base">Today</CardTitle>
            {day && <p className="text-xs text-muted-foreground">{dayHeading(day.date)}</p>}
          </div>
          <Badge variant="outline" className={statusTone(summary)}>
            {statusLabel(summary)}
          </Badge>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
            {employee?.primarySite && (
              <span className="flex items-center gap-1.5">
                <Building2 className="h-4 w-4" />
                {employee.primarySite.name}
              </span>
            )}
            {hoursLabel && (
              <span className="flex items-center gap-1.5">
                <Clock className="h-4 w-4" />
                <span>{hoursLabel}</span>
              </span>
            )}
          </div>
          {/* BR-202: not scheduled means not expected, never "closed". */}
          {notScheduled && (action === "check-in" || action === "check-in-again") && (
            <p className="text-xs text-muted-foreground">
              You can still check in — it records as usual.
            </p>
          )}

          <div className="grid grid-cols-3 gap-2 rounded-lg border p-3 text-center">
            <Stat label="In" value={clock(summary.firstInAt)} />
            <Stat label="Out" value={clock(summary.lastOutAt)} />
            <Stat
              label="Hours"
              value={summary.workedMinutes > 0 ? formatDuration(summary.workedMinutes) : "—"}
            />
          </div>
          {shortReasons.length > 0 && (
            <p className="text-sm text-warning">{shortReasons.join(" · ")}</p>
          )}

          {action === "done" ? (
            // BR-021: a one-session organisation refuses a second check-in, so
            // offering one only led to that refusal (BUG-2026-013).
            <p className="text-sm text-muted-foreground">
              You've completed today's attendance. If something looks wrong,{" "}
              <Link to="/me/requests" className="font-medium text-primary hover:underline">
                raise a correction request
              </Link>
              .
            </p>
          ) : !webEnabled ? (
            // BR-151. The server refuses these routes anyway; this is just so
            // the employee isn't left pressing a button that cannot work.
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Web check-in is off for your organisation</AlertTitle>
              <AlertDescription>
                Use the GeoKerja mobile app to check in and out. Your attendance
                history is still shown here.
              </AlertDescription>
            </Alert>
          ) : (
            <Button
              className="w-full"
              size="lg"
              disabled={punch !== null}
              onClick={() => setPunch(action === "check-out" ? "out" : "in")}
            >
              {action === "check-out" ? (
                <LogOut className="mr-2 h-4 w-4" />
              ) : (
                <LogIn className="mr-2 h-4 w-4" />
              )}
              {action === "check-out"
                ? "Check out"
                : action === "check-in-again"
                  ? "Check in again"
                  : "Check in"}
            </Button>
          )}
        </CardContent>
      </Card>

      {webEnabled && (
        <PunchDialog
          kind={punch}
          action={action}
          tz={tz}
          maxAccuracyM={config?.attendance.maxCheckInAccuracyM ?? 100}
          site={
            // A check-out is judged against the session's own site; a check-in
            // against the primary one, which is what the server uses.
            (punch === "out"
              ? sites.find((x) => x.id === openSession?.siteId)
              : sites.find((x) => x.id === employee?.primarySite?.id)) ??
            sites[0] ??
            null
          }
          openSession={openSession}
          shortfall={punch === "out" ? projectedShortfall(day, summary) : null}
          onClose={() => setPunch(null)}
          onPunched={onPunched}
        />
      )}

      {/* Only where the web can punch: the verdict comes from the web preview
          route, which the server refuses when web check-in is off (BR-151). */}
      {webEnabled && (
        <LocationCheckCard
          tz={tz}
          maxAccuracyM={config?.attendance.maxCheckInAccuracyM ?? 100}
          action={action}
        />
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Today's sessions</CardTitle>
        </CardHeader>
        <CardContent>
          {day?.sessions.length ? (
            day.sessions.map((s) => <SessionRow key={s.id} session={s} tz={tz} />)
          ) : (
            <p className="py-2 text-sm text-muted-foreground">
              Nothing recorded yet today.
            </p>
          )}
        </CardContent>
      </Card>

      {/* The rest of the lane, reachable from the screen people actually land
          on. The nav carries the same destinations, but someone opening the app
          to clock in should not have to hunt for "where do I ask for leave". */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <QuickLink to="/me/attendance" icon={CalendarDays} label="My attendance" hint="Past 30 days" />
        <QuickLink to="/me/schedule" icon={CalendarRange} label="Schedule" hint="Next 14 days" />
        <QuickLink to="/me/requests" icon={FileCheck2} label="Requests" hint="Leave, corrections" />
        <QuickLink
          to="/me/notifications"
          icon={Bell}
          label="Alerts"
          hint={unread > 0 ? `${unread} unread` : "Nothing new"}
          badge={unread}
        />
      </div>

      <AnnouncementsCard tz={tz} />
    </div>
  );
}

function QuickLink({
  to,
  icon: Icon,
  label,
  hint,
  badge = 0,
}: {
  to: string;
  icon: typeof Bell;
  label: string;
  hint: string;
  badge?: number;
}) {
  return (
    <Link
      to={to}
      className="relative flex flex-col gap-1 rounded-lg border bg-card p-3 transition-colors hover:bg-secondary"
    >
      <Icon className="h-4 w-4 text-muted-foreground" />
      <span className="text-sm font-medium leading-tight">{label}</span>
      <span className="text-xs text-muted-foreground leading-tight">{hint}</span>
      {badge > 0 && (
        <span className="absolute right-2 top-2 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[9px] font-semibold text-destructive-foreground">
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </Link>
  );
}
